/**
 * SwapExerciseModal — Permet de remplacer un exercice IA par un exercice de la bibliotheque DB.
 * Recherche + filtre par groupe musculaire, clic pour selectionner.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../context/AppContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Search, X, Loader2, Dumbbell, ArrowRightLeft, ChevronDown
} from 'lucide-react';

export default function SwapExerciseModal({ isOpen, onClose, onSelect, currentExerciseName, language }) {
  const fr = language === 'fr';
  const [search, setSearch] = useState('');
  const [muscleGroup, setMuscleGroup] = useState('');
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(null);
  const searchTimeout = useRef(null);

  // Fetch muscle group filters once
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const res = await api.get('/workouts/filters');
        setFilters(res.data);
      } catch { /* ignore */ }
    })();
  }, [isOpen]);

  const fetchExercises = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (muscleGroup) params.set('muscle_group', muscleGroup);
      params.set('limit', '50');
      const res = await api.get(`/workouts/exercises?${params}`);
      setExercises(res.data.exercises || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [search, muscleGroup]);

  useEffect(() => {
    if (!isOpen) return;
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => fetchExercises(), 300);
    return () => { if (searchTimeout.current) clearTimeout(searchTimeout.current); };
  }, [isOpen, fetchExercises]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setMuscleGroup('');
      setExercises([]);
      fetchExercises();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const muscleGroups = filters?.muscle_groups || [];

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center" data-testid="swap-exercise-modal">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full sm:max-w-lg max-h-[85vh] bg-card border border-border rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden z-10">
        {/* Header */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <ArrowRightLeft className="w-4 h-4 text-primary" />
              <h3 className="font-heading text-sm font-bold uppercase tracking-wide">
                {fr ? 'Remplacer l\'exercice' : 'Swap Exercise'}
              </h3>
            </div>
            <Button size="sm" variant="ghost" onClick={onClose} className="h-7 w-7 p-0" data-testid="close-swap-modal">
              <X className="w-4 h-4" />
            </Button>
          </div>

          {currentExerciseName && (
            <p className="text-[11px] text-muted-foreground mb-2">
              {fr ? 'Remplacer' : 'Replace'}: <span className="text-foreground font-medium">{currentExerciseName}</span>
            </p>
          )}

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={fr ? 'Rechercher un exercice...' : 'Search exercise...'}
              className="h-8 text-xs pl-8"
              data-testid="swap-search-input"
              autoFocus
            />
          </div>

          {/* Muscle group filter */}
          {muscleGroups.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              <button
                onClick={() => setMuscleGroup('')}
                className={`text-[10px] px-2 py-0.5 rounded-full transition-colors ${
                  !muscleGroup ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground hover:text-foreground'
                }`}
                data-testid="swap-filter-all"
              >
                {fr ? 'Tous' : 'All'}
              </button>
              {muscleGroups.map(mg => (
                <button
                  key={mg}
                  onClick={() => setMuscleGroup(mg === muscleGroup ? '' : mg)}
                  className={`text-[10px] px-2 py-0.5 rounded-full transition-colors ${
                    muscleGroup === mg ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid={`swap-filter-${mg}`}
                >
                  {mg}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Exercise List */}
        <div className="flex-1 overflow-y-auto p-2 space-y-1" data-testid="swap-exercise-list">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-primary" />
            </div>
          ) : exercises.length === 0 ? (
            <div className="text-center py-8">
              <Dumbbell className="w-8 h-8 text-muted-foreground/20 mx-auto mb-2" />
              <p className="text-xs text-muted-foreground">{fr ? 'Aucun exercice trouve' : 'No exercises found'}</p>
            </div>
          ) : (
            exercises.map(ex => (
              <button
                key={ex.exercise_id}
                onClick={() => {
                  onSelect(fr ? ex.name_fr : (ex.name_en || ex.name_fr));
                  onClose();
                }}
                className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-secondary/30 hover:bg-primary/10 hover:ring-1 hover:ring-primary/30 transition-all text-left group"
                data-testid={`swap-pick-${ex.exercise_id}`}
              >
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <Dumbbell className="w-4 h-4 text-primary/60" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-foreground truncate">
                    {fr ? ex.name_fr : (ex.name_en || ex.name_fr)}
                  </p>
                  <div className="flex flex-wrap gap-1 mt-0.5">
                    {ex.muscle_groups?.slice(0, 3).map(mg => (
                      <span key={mg} className="text-[9px] px-1.5 py-0 bg-primary/10 text-primary/70 rounded">{mg}</span>
                    ))}
                    <span className="text-[9px] px-1.5 py-0 bg-secondary text-muted-foreground rounded">
                      {ex.equipment || ''}
                    </span>
                  </div>
                </div>
                <ArrowRightLeft className="w-3.5 h-3.5 text-muted-foreground/30 group-hover:text-primary transition-colors shrink-0" />
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
